/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useEffect, useRef, useState, useContext, useMemo, useCallback } from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { followCursor } from 'tippy.js';
import TextField from '@airwallex/form-textfield/formik';
import { VStack } from '@airwallex/stacks';
import { init, Payment as PaymentNamespace, Env } from '@airwallex/components-sdk';
import useMessageEvent from '@/hooks/useMessageEvent';
import { StoreContext } from '@/providers/storeProvider';
import { useTranslation } from 'react-i18next';
import { getPageEnvByHost } from '@/utils/environment';
import { getFirstAndLastName, IPaymentElement } from '@/utils/dropin';
import { generateDropInElement } from '@/utils/dropin';
import { getCustomerDisplayName } from '@/utils/customer';
import { getInvoiceStatus } from '@/api';
import { useToasts } from '@airwallex/toast';
import { confirmIntent, updateIntent } from '@/api/intent';
import CustomSpinner from '@/common/Spinner';
import { Overlay } from './style';
import { airTracker } from '@/utils/airTracker';
import { trace } from '@/utils/trace';
import { COMPONENT_NAME } from '@/pages/Main';
import i18n from '@/utils/i18n';
import { toPaymentInitLocale, toPaymentElementUpdateLocale } from '@/utils/paymentSdkLocale';
import { CustomerInfoForm } from '../Layout';
import { useFocusOnError } from '@/hooks/useFocusOnError';
import { useDisablePay } from '@/hooks/useDisablePay';
import { handleKeyDownEnter } from '@/utils/keydown';
import { useFormikContext } from 'formik';
import { validateEmailField, validateNameField } from '@/utils/validate';
import { eventGa4 } from '@/utils/google';
import { usePerformanceMeasure } from '@/hooks/usePerformanceMeasure';

export const PAY_EVENT_CODES = [
  'onRedirect',
  'onApplePayButtonClick',
  'onGooglePayButtonClick',
  'onClickConfirmButton',
];

function initialize() {
  return init({
    env: getPageEnvByHost() as Env,
    locale: toPaymentInitLocale(i18n.language),
    enabledElements: ['dropIn'],
  });
}
const initTask = initialize();

function buildHostedInvoiceDropInElement(params: {
  intentId: string;
  clientSecret: string;
  currency: string;
  countryCode: string | undefined;
  merchantCountryCode: string | undefined;
  shopperName: string | undefined;
  shopperEmail: string;
  customerId: string | undefined;
  shouldCreateMITConsent: boolean;
  disablePay: boolean | undefined;
  paymentMethodSave: IPaymentElement['paymentMethodSave'];
  paymentMethods: IPaymentElement['paymentMethods'];
  gaOrderId: string | undefined;
  firstName: string;
  lastName: string;
  address: {
    city: string;
    country_code: string;
    postcode: string;
    state: string;
    street: string;
  };
}): IPaymentElement {
  return {
    intent_id: params.intentId,
    client_secret: params.clientSecret,
    currency: params.currency,
    countryCode: params.countryCode,
    merchantCountryCode: params.merchantCountryCode,
    shopper_name: params.shopperName,
    shopper_email: params.shopperEmail,
    customer_id: params.customerId,
    shouldCreateMITConsent: params.shouldCreateMITConsent,
    disablePay: params.disablePay,
    paymentMethodSave: params.paymentMethodSave,
    paymentMethods: params.paymentMethods,
    ...(params.gaOrderId ? { gaOrderId: params.gaOrderId } : {}),
    billing: {
      email: params.shopperEmail,
      first_name: params.firstName,
      last_name: params.lastName,
      address: params.address,
    },
  };
}

const Payment = () => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const componentMountTimeRef = useRef<number>(performance.now());
  const onSuccessTimeRef = useRef<number | null>(null);
  const [isPaying, setIsPaying] = useState(false);
  const [errorToastMessage, setErrorToastMessage] = useState('');
  const { customer, invoiceData, quote, signature, country } = useContext(StoreContext);
  const { invoice, secret, transaction, customerSnapshot, shouldCreateMITConsent, linkedPaymentAccountCountryCode } =
    invoiceData;
  const { currency, paymentOptions, availablePaymentMethodTypes = [], paymentMethodInfo } = invoice;
  const gaOrderId = paymentMethodInfo?.billingPayment?.bankTransfer?.gaOrderId;
  // Bank transfer (ga_order_id): skip `updateIntent` entirely — no `customer`/`return_url` payload; those calls break this flow.
  const omitCustomerFromIntentUpdate = Boolean(gaOrderId);
  const { paymentMethodTypes = [] } = paymentOptions || {};

  const methods = useMemo(
    () => (paymentMethodTypes.length > 0 ? paymentMethodTypes : availablePaymentMethodTypes),
    [paymentMethodTypes, availablePaymentMethodTypes]
  );
  const { values, setFieldError } = useFormikContext<CustomerInfoForm>();
  const { disablePay, triggerValidation } = useDisablePay();
  const [focusTrigger, setFocusTrigger] = useState(0);
  const triggerFocusOnError = useCallback(() => setFocusTrigger((prev) => prev + 1), []);

  const { registerField, focusOnFirstError } = useFocusOnError();
  useEffect(() => {
    if (focusTrigger > 1) {
      focusOnFirstError();
    }
  }, [focusOnFirstError, focusTrigger]);

  const elementRef = useRef<PaymentNamespace.DropInElementType | null>(null);
  const [paymentElementReady, setPaymentElementReady] = useState(false);
  const isProcessingPayment = useRef(false);

  const customerDisplayName = getCustomerDisplayName(customerSnapshot);
  const customerEmail = customerSnapshot?.email || '';
  const { firstName, lastName } = getFirstAndLastName(customerDisplayName || values.name || '');
  const customerUpdate = useMemo(() => {
    return {
      ...(customer || {}),
      first_name: firstName,
      last_name: lastName,
      business_name:
        customerSnapshot?.type === 'BUSINESS'
          ? customerSnapshot?.name || customerSnapshot?.nickname || values.name
          : undefined,
      email: values.email || customerSnapshot?.email,
      merchant_customer_id: customerSnapshot?.id,
      phone_number: customerSnapshot?.phoneNumber,
      address: customerSnapshot?.address?.countryCode
        ? {
            country_code: customerSnapshot?.address?.countryCode,
            street: customerSnapshot?.address?.street,
            city: customerSnapshot?.address?.city,
            state: customerSnapshot?.address?.state,
            postcode: customerSnapshot?.address?.postcode,
          }
        : undefined,
    };
  }, [
    customer,
    customerSnapshot?.address?.city,
    customerSnapshot?.address?.countryCode,
    customerSnapshot?.address?.postcode,
    customerSnapshot?.address?.state,
    customerSnapshot?.address?.street,
    customerSnapshot?.email,
    customerSnapshot?.id,
    customerSnapshot?.name,
    customerSnapshot?.nickname,
    customerSnapshot?.phoneNumber,
    customerSnapshot?.type,
    values.email,
    values.name,
    firstName,
    lastName,
  ]);

  // do this immediately before render
  trace.addTTVRDependency('paymentElementReady-dropIn');

  useEffect(() => {
    if (errorToastMessage) {
      addToast(errorToastMessage, { variant: 'CRITICAL' });
    }
  }, [addToast, errorToastMessage]);
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const { data } = await getInvoiceStatus(signature);

        if (data.status === 'FINALIZED' && data.paymentStatus === 'PAID') {
          if (onSuccessTimeRef.current) {
            const finalizedTime = performance.now();
            const successToFinalizedLatency = finalizedTime - onSuccessTimeRef.current;

            airTracker.info('payment-duration', {
              component: COMPONENT_NAME,
              successToFinalizedLatency: Math.round(successToFinalizedLatency),
              onSuccessTime: onSuccessTimeRef.current,
              finalizedTime,
              invoiceStatus: data.status,
              paymentStatus: data.paymentStatus,
            });
          }

          window.location.reload();
        }
      } catch (error: any) {
        airTracker.error('getInvoice', {
          component: COMPONENT_NAME,
          error: error?.detail,
          detail: error?.detail?.error,
          type: 'reload',
        });
        window.location.reload();
      }
    }, 5000);
    return () => {
      clearInterval(interval);
    };
  }, [signature]);

  usePerformanceMeasure({
    name: 'paymentElementReady-dropIn',
    eventName: 'paymentElementReady',
    ready: paymentElementReady,
    payload: {
      element: 'dropIn',
    },
  });

  useMessageEvent('onReady', () => {
    const readyTime = performance.now();
    const dropInLatency = readyTime - componentMountTimeRef.current;

    airTracker.info('onReady', {
      component: COMPONENT_NAME,
      dropInLatency: Math.round(dropInLatency),
      componentMountTime: Math.round(componentMountTimeRef.current),
      readyTime: Math.round(readyTime),
    });

    setPaymentElementReady(true);
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useMessageEvent('onError', (error: any) => {
    airTracker.error('onError', { component: COMPONENT_NAME, error: error?.detail, detail: error?.detail?.error });
    if (error?.detail?.error?.message !== errorToastMessage) {
      setErrorToastMessage(error?.detail?.error?.message);
    }
  });
  useMessageEvent('message', (message) => {
    // TODO remove
    airTracker.info('message', { component: COMPONENT_NAME, message });
    airTracker.info('paymentElementMessage', {
      component: COMPONENT_NAME,
      data: message.data,
      type: message.data?.type,
      code: message.data?.code,
      time: performance.now(),
    });
  });

  useMessageEvent('message', (e) => {
    if (e.isTrusted && e.data?.type === 'dropIn' && PAY_EVENT_CODES.includes(e.data?.code)) {
      airTracker.info('hosted_invoice_pay_click', {
        component: COMPONENT_NAME,
        message: e.data,
      });
      eventGa4('click', {
        code: e.data?.code,
      });
    }
    if (e.data?.code === 'onClickDisabledButton') {
      triggerFocusOnError();
    }
  });
  elementRef.current?.on('success', async (event: any) => {
    onSuccessTimeRef.current = performance.now();

    // TODO remove
    airTracker.info('onSuccess', {
      component: COMPONENT_NAME,
      message: event?.detail,
      onSuccessTime: onSuccessTimeRef.current,
    });
    airTracker.info('paymentElementOnSuccess', {
      component: COMPONENT_NAME,
      type: event?.detail?.type,
      data: event?.detail,
      time: event?.timeStamp,
    });

    setIsPaying(true);
    const { consent, intent } = event.detail;
    // LPM will only verify consent, won't confirm intent, so we need to confirm it manually
    if (secret && intent?.id && consent?.id && !isProcessingPayment.current) {
      isProcessingPayment.current = true;
      try {
        await confirmIntent(secret, intent.id, {
          payment_consent_id: consent.id,
        });
      } catch (error) {
        setIsPaying(false);
        addToast(t('error.paymentFailed'), { variant: 'CRITICAL' });
        // Reset the flag if confirmIntent fails, allowing retry
        isProcessingPayment.current = false;
        throw error;
      }
    }
  });
  useEffect(() => {
    (async () => {
      if (!secret || !transaction?.externalId) {
        return;
      }
      try {
        if (!omitCustomerFromIntentUpdate) {
          await updateIntent(secret, transaction.externalId, {
            return_url: window.location.href,
            customer: customerUpdate,
          });
        }

        await initTask;

        const paymentElement = buildHostedInvoiceDropInElement({
          intentId: transaction.externalId,
          clientSecret: secret,
          currency: quote?.target_currency || currency,
          countryCode: customerSnapshot?.address?.countryCode || country,
          merchantCountryCode: linkedPaymentAccountCountryCode,
          shopperName: customerDisplayName,
          shopperEmail: customerEmail,
          customerId: transaction.customerExternalId,
          shouldCreateMITConsent,
          disablePay,
          paymentMethodSave: invoice?.paymentOptions?.paymentMethodSave,
          paymentMethods: methods as PaymentNamespace.PaymentMethodType[],
          gaOrderId,
          firstName,
          lastName,
          address: {
            city: customerSnapshot?.address?.city || '',
            country_code: customerSnapshot?.address?.countryCode || '',
            postcode: customerSnapshot?.address?.postcode || '',
            state: customerSnapshot?.address?.state || '',
            street: customerSnapshot?.address?.street || '',
          },
        });

        const element = await generateDropInElement(paymentElement);
        element?.mount('drop-in');
        elementRef.current = element as unknown as PaymentNamespace.DropInElementType;
      } catch (error: any) {
        addToast(error?.response?.data?.message || t('error.init'), { variant: 'CRITICAL' });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secret, transaction?.externalId]);

  useEffect(() => {
    const language = i18n.language;
    if (paymentElementReady) {
      elementRef.current?.update(
        {
          // @ts-ignore
          layout: {
            type: 'accordion',
            alwaysShowMethodLabel: true,
          },
        },
        {
          locale: toPaymentElementUpdateLocale(language),
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentElementReady, i18n.language]);

  useEffect(() => {
    if (paymentElementReady && elementRef.current) {
      const element = elementRef.current as unknown as PaymentNamespace.DropInElementType;

      if (shouldCreateMITConsent) {
        element.on('switchMethod', (event: any) => {
          const { method } = event.detail;
          if (method === 'klarna') {
            elementRef.current?.update({
              payment_consent: {
                next_triggered_by: 'merchant',
                terms_of_use: {
                  payment_amount_type: 'VARIABLE',
                  // @ts-ignore
                  min_payment_amount: 0,
                },
              },
              layout: {
                type: 'accordion',
                alwaysShowMethodLabel: true,
              },
            });
          } else {
            elementRef.current?.update({
              payment_consent: {
                next_triggered_by: 'merchant',
              },
              layout: {
                type: 'accordion',
                alwaysShowMethodLabel: true,
              },
            });
          }
        });
      }
    }
  }, [elementRef, paymentElementReady, shouldCreateMITConsent]);

  const handleValidation = async (fieldName: string) => {
    const error = fieldName === 'name' ? validateNameField(values.name) : validateEmailField(values.email);
    setFieldError(fieldName, error);
    const currentDisablePay = triggerValidation();
    elementRef.current?.update({
      billing: {
        email: values.email || '',
        first_name: firstName || '',
        last_name: lastName || '',
        address: {
          city: '',
          country_code: '',
          postcode: '',
          state: '',
          street: '',
        },
      },
      // drop-in uses shopper_name and shopper_email to validate whether show redirect form fields
      shopper_name: `${firstName || ''} ${lastName || ''}`.trim(),
      shopper_email: values.email,
      // @ts-ignore
      disablePay: currentDisablePay,
      layout: {
        type: 'accordion',
        alwaysShowMethodLabel: true,
      },
    });
    airTracker.info('dropInUpdate', { component: COMPONENT_NAME, disablePay: currentDisablePay });

    if (
      !omitCustomerFromIntentUpdate &&
      paymentElementReady &&
      !currentDisablePay &&
      secret &&
      transaction?.externalId
    ) {
      airTracker.info('updateCustomer', {
        component: COMPONENT_NAME,
      });
      await updateIntent(secret, transaction?.externalId, {
        customer: customerUpdate,
      }).catch((error) => {
        addToast(error?.response?.data?.message || t('error.update_intent'), { variant: 'CRITICAL' });
      });
    }
  };
  return (
    <div style={{ width: '100%', position: 'relative' }}>
      <VStack space="16px" style={{ marginBottom: '24px' }} align="STRETCH">
        {!customerDisplayName && (
          <TextField
            ref={registerField('name')}
            label={t('customer.name')}
            name="name"
            onBlur={() => {
              handleValidation('name');
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              handleKeyDownEnter(e, () => e.currentTarget.blur());
            }}
          />
        )}
        {!customerEmail && (
          <TextField
            ref={registerField('email')}
            label={t('customer.email')}
            name="email"
            onBlur={() => {
              handleValidation('email');
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              handleKeyDownEnter(e, () => e.currentTarget.blur());
            }}
          />
        )}
      </VStack>
      <div style={{ position: 'relative' }}>
        <Tippy
          content={t('tooltip.disable.pay')}
          followCursor={true}
          plugins={[followCursor]}
          disabled={!disablePay}
          arrow={false}
          maxWidth={266}
        >
          <div style={{ cursor: 'not-allowed' }}>
            <div id="drop-in" data-test="dropIn" style={{ width: '100%' }}></div>
            {disablePay && <Overlay isTransparent={true} onClick={triggerFocusOnError} />}
          </div>
        </Tippy>
      </div>
      {(isPaying || !paymentElementReady) && (
        <Overlay>
          <CustomSpinner />
        </Overlay>
      )}
    </div>
  );
};

export default Payment;
